import { toast } from "sonner";
import { Package, Truck, Check } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";

const TAGS = {
  packed: { label: "Packed", cls: "bg-sky-500/15 border-sky-400/40 text-sky-200" },
  shipped: { label: "Shipped", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
};

export const ShipTag = ({ status, testId }) => {
  const t = TAGS[status];
  if (!t) return null;
  return <span data-testid={testId} className={`self-start sm:self-auto shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${t.cls}`}>{t.label}</span>;
};

export const markShip = async (ids, status, done) => {
  try {
    if (ids.length === 1) await api.put(`/higher/joiners/${ids[0]}/ship`, { status });
    else await api.post("/higher/ship-bulk", { joiner_ids: ids, status });
    toast.success(status ? `Marked ${TAGS[status].label}` : "Packing status cleared");
    done();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Step = ({ icon: Icon, label, on, at, testId }) => (
  <div data-testid={testId} className="flex items-center gap-2.5">
    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors ${on ? "border-emerald-400 bg-emerald-500/20 text-emerald-200" : "border-zinc-700 text-zinc-600"}`}>
      {on ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
    </span>
    <div>
      <div className={`text-sm font-semibold ${on ? "text-white" : "text-zinc-500"}`}>{label}</div>
      {on && at && <div className="text-[11px] text-zinc-500">{fmtDate(at)}</div>}
    </div>
  </div>
);

const AdminButtons = ({ j, onChanged }) => (
  <div className="flex flex-wrap gap-2">
    {[["packed", "Mark Packed"], ["shipped", "Mark Shipped"], ["", "Clear"]].map(([s, label]) => (
      <button key={label} type="button" data-testid={`higher-ship-set-${s || "clear"}`} disabled={j.ship_status === s} onClick={() => markShip([j.id], s, onChanged)}
        className="rounded-full border border-zinc-700 px-3 py-1 text-xs font-semibold text-zinc-300 transition-colors hover:border-sky-400/60 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed">
        {label}
      </button>
    ))}
  </div>
);

export default function HigherShipTracker({ j, isAdmin, onChanged }) {
  const s = j.ship_status;
  return (
    <section data-testid="higher-ship-tracker" className="mt-6 rounded-2xl border border-white/10 bg-zinc-900/60 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-base md:text-lg font-bold">Order Status</h2>
        {isAdmin && <AdminButtons j={j} onChanged={onChanged} />}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Step icon={Package} label="Packed" on={!!s} at={j.packed_at} testId="higher-ship-step-packed" />
        <div className={`h-0.5 flex-1 max-w-24 rounded-full ${s === "shipped" ? "bg-emerald-400" : "bg-zinc-800"}`} />
        <Step icon={Truck} label="Shipped" on={s === "shipped"} at={j.shipped_at} testId="higher-ship-step-shipped" />
      </div>
      {!s && <p data-testid="higher-ship-pending" className="mt-3 text-xs text-zinc-500">Not packed yet. This updates once your items are packed and sent out.</p>}
    </section>
  );
}
